import { useEffect, useState } from 'react';
import { Heart, Trash2 } from 'lucide-react';
import {
  fetchAdminFriendships, voidAdminFriendship,
  type AdminFriendship,
} from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AdminList.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'blocked', label: 'Blocked' },
];

function statusClass(status: string): string {
  if (status === 'accepted') return styles.badgeOk!;
  if (status === 'pending') return styles.badgePending!;
  if (status === 'blocked') return styles.badgeError!;
  return styles.badgeMuted!;
}

export function FriendshipsPage() {
  useRecentRoute('Friendships');
  const [rows, setRows] = useState<AdminFriendship[]>([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchAdminFriendships(status || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [status]);

  const handleVoid = async (row: AdminFriendship) => {
    if (!window.confirm(
      `Void the ${row.status} friendship between ${row.requester.name} and ${row.recipient.name}? This is irreversible.`,
    )) return;
    try {
      await voidAdminFriendship(row.id);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to void');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Heart size={18} /></div>
          <div>
            <h1 className={styles.title}>Friendships</h1>
            <p className={styles.subtitle}>
              {rows.length} row{rows.length === 1 ? '' : 's'}
              {status ? ` · filtered by ${status}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && rows.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No friendships</p>
          <p>Nothing matches this filter.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Requester</th>
                <th>Recipient</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Accepted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>{r.requester.name}</span>
                      <span className={styles.userSub}>
                        {r.requester.username ? `@${r.requester.username}` : r.requester.email}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>{r.recipient.name}</span>
                      <span className={styles.userSub}>
                        {r.recipient.username ? `@${r.recipient.username}` : r.recipient.email}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${statusClass(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{new Date(r.requestedAt).toLocaleDateString()}</td>
                  <td>{r.acceptedAt ? new Date(r.acceptedAt).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.dangerBtn}
                      onClick={() => void handleVoid(r)}
                      title="Void this friendship"
                    >
                      <Trash2 size={12} /> Void
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
